import { Controller } from "@hotwired/stimulus";
import { navigateAfterLegalConfirmation } from "../../helpers/legalNavigation";
import Bridge from "../../turbo/bridge";

const VALIDATION_CLASSES = ["ring-2", "ring-danger", "rounded"] as const;

// Connects to data-controller="shared--legal-checkboxes"
export default class extends Controller<HTMLFormElement> {
  connect() {
    // Remove Rails' auto-disable on submit buttons so we can prevent submission
    // without the button getting stuck in disabled state
    this.element
      .querySelectorAll("[data-disable-with]")
      .forEach((el) => el.removeAttribute("data-disable-with"));

    this.element.addEventListener("submit", this.validate);
    this.element.addEventListener("change", this.clearValidation);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.validate);
    this.element.removeEventListener("change", this.clearValidation);
  }

  validate = (event: Event) => {
    const checkboxes = this.element.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][data-legal-checkbox]',
    );
    let allChecked = true;

    checkboxes.forEach((cb) => {
      if (!cb.checked) {
        allChecked = false;
        cb.classList.add(...VALIDATION_CLASSES);
      }
    });

    if (!allChecked) {
      event.preventDefault();
      toastr.error(I18n.t("js.legal_checkboxes.validation_error"));
      return;
    }

    // Inside the app the native form POST goes nowhere: /legal lives there
    // as a Turbo snapshot in the shared tab session, which neither proposes
    // raw submits as a visit nor performs them (#104). fetch + explicit
    // navigation work reliably instead — including in old app versions.
    if (window.isApp) {
      event.preventDefault();
      this.submitViaFetch();
    }
  };

  async submitViaFetch() {
    const token = document
      .querySelector("meta[name=csrf-token]")
      ?.getAttribute("content");
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        body: new FormData(this.element),
        headers: token ? { "X-CSRF-Token": token } : {},
        credentials: "same-origin",
        redirect: "follow",
      });
      if (!response.ok) throw new Error(String(response.status));
      // Back to the page the gate redirected away from (sign-in flow with
      // no remembered page: start page).
      navigateAfterLegalConfirmation();
      // Catch up on the native login handover: in the sign-in flow the app
      // waits for isLoggedIn to switch from the login webview to the tab
      // view. App.tsx suppresses that message on /legal (premature
      // handover), and without a full page load it would never fire. Send
      // it only AFTER the navigate: while the webview URL is /legal,
      // redirectLogin() ignores the message. In the already-signed-in
      // content context it is a no-op.
      Bridge.setMemberIsLoggedIn(true);
    } catch {
      (toastr as { error: (msg: string) => void }).error(
        (I18n as { t: (key: string) => string }).t("js.saving_failure"),
      );
    }
  }

  clearValidation = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (
      target.type === "checkbox" &&
      target.dataset.legalCheckbox !== undefined &&
      target.checked
    ) {
      target.classList.remove(...VALIDATION_CLASSES);
    }
  };
}
